// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SentimentVeryDissatisfiedRoundedIcon from '@mui/icons-material/SentimentVeryDissatisfiedRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import SentimentSatisfiedRoundedIcon from '@mui/icons-material/SentimentSatisfiedRounded';
import SentimentVerySatisfiedRoundedIcon from '@mui/icons-material/SentimentVerySatisfiedRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import { useTranslation } from 'react-i18next';
import { useAuth, useTeams, useBonusRevenueTypes } from '../../../hooks';
import { hasuraClient } from '../../../utils/graphqlClient';
import { GET_BONUS_TARGETS_QUERY } from '../../../graphql/queries/primYonetimi';
import { GET_USER_REVENUE_ENTRIES_QUERY } from '../../../graphql/queries/userRevenueEntries';
import { ciroKontrolPaneliStyles as styles, KP_PALETTE } from './CiroKontrolPaneli.styles';

// Mevcut yil + ceyrek (1..4)
const getCurrentPeriod = () => {
  const now = new Date();
  return {
    yil: now.getFullYear(),
    ceyrek: Math.floor(now.getMonth() / 3) + 1,
  };
};

// Bu paneli yalnizca asagidaki roller gorebilir.
// Pano herkesce gorulebilir — ekip uyeleri de kendi ekibinin verisini gorur.
// Ekip dropdown'u sadece ROOT'a gosterilir; diger rollere kendi team_id'si
// otomatik secilir ve dropdown gizlenir.
export const ALLOWED_ROLES = ['ROOT', 'DIRECTOR', 'GROUP_MANAGER', 'TEAM_MANAGER', 'TEAM_MEMBER'];

const QUARTER_OPTIONS = (() => {
  const { yil, ceyrek } = getCurrentPeriod();
  const options = [];
  let y = yil;
  let q = ceyrek;
  for (let i = 0; i < 6; i += 1) {
    options.push({ yil: y, ceyrek: q, label: `${y} / Q${q}` });
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return options;
})();

// TR locale ile binlik ayraci.
const formatMoney = (val) => {
  const n = Number(val || 0);
  return n.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Gauge — donut with a single accent arc on a light track. Rotated -90deg so
// the fill starts from the top. Implemented with stroke-dasharray instead of
// arc paths to keep the line smooth and rounded.
// Animates from 0 → target pct on mount (and when pct/color change) via
// rAF interpolation. ~1.2s ease-out.
const Gauge = ({ pct, color }) => {
  const size = 172;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const safePct = Math.min(100, Math.max(0, pct));

  const [animatedPct, setAnimatedPct] = useState(0);

  useEffect(() => {
    // Reset to 0 and animate to safePct over ~1.2s with an ease-out curve.
    const duration = 1200;
    const start = performance.now();
    const from = 0;
    const to = safePct;
    let rafId;
    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedPct(from + (to - from) * eased);
      if (t < 1) {
        rafId = requestAnimationFrame(step);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [safePct]);

  const dash = (animatedPct / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#eef0f4"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
        />
      </g>
    </svg>
  );
};

const CiroKontrolPaneli = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const isAllowed = ALLOWED_ROLES.includes(user?.role);
  // Ekip dropdown'u sadece ROOT'a gosterilir; diger roller kendi ekibinin
  // verilerini gorur ve baska bir ekibe gecemez.
  const isRoot = user?.role === 'ROOT';

  // Default filtreler — mevcut ceyrek + kullanicinin kendi ekibi.
  const { yil: defaultYil, ceyrek: defaultCeyrek } = useMemo(getCurrentPeriod, []);
  const [selectedYilCeyrek, setSelectedYilCeyrek] = useState(
    `${defaultYil}-${defaultCeyrek}`
  );
  const [selectedTeamId, setSelectedTeamId] = useState(
    user?.team_id ? String(user.team_id) : ''
  );

  useEffect(() => {
    if (!selectedTeamId && user?.team_id) {
      setSelectedTeamId(String(user.team_id));
    }
  }, [user?.team_id, selectedTeamId]);

  const { teams, fetchData: fetchTeams } = useTeams();
  const { bonusRevenueTypes, fetchData: fetchBonusRevenueTypes } = useBonusRevenueTypes();

  useEffect(() => {
    if (!isAllowed) return;
    fetchTeams();
    fetchBonusRevenueTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const coefficientById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      const c = Number(b?.coefficient);
      map.set(b.id, Number.isFinite(c) ? c : 1);
    });
    return map;
  }, [bonusRevenueTypes]);

  const { yil, ceyrek } = useMemo(() => {
    const [y, q] = selectedYilCeyrek.split('-');
    return { yil: parseInt(y, 10), ceyrek: parseInt(q, 10) };
  }, [selectedYilCeyrek]);

  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(0);
  const [entries, setEntries] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  const teamIdNum = useMemo(() => {
    const n = parseInt(selectedTeamId, 10);
    return Number.isFinite(n) ? n : null;
  }, [selectedTeamId]);

  const fetchPanelData = useCallback(async () => {
    if (!isAllowed) return;
    if (!teamIdNum || !Number.isFinite(yil) || !Number.isFinite(ceyrek)) {
      setTarget(0);
      setEntries([]);
      return;
    }
    setLoading(true);
    setErrorMsg(null);
    try {
      const targetsRes = await hasuraClient.query(GET_BONUS_TARGETS_QUERY, {
        where: {
          team_id: { _eq: teamIdNum },
          yil: { _eq: yil },
          ceyrek: { _eq: ceyrek },
        },
      });
      const targetsList = targetsRes?.bonus_targets || [];
      const totalTarget = targetsList.reduce(
        (sum, row) => sum + Number(row?.hedef_tutar || 0),
        0
      );

      const entriesRes = await hasuraClient.query(GET_USER_REVENUE_ENTRIES_QUERY, {
        where: {
          status: { _eq: 'APPROVED' },
          yil: { _eq: yil },
          ceyrek: { _eq: ceyrek },
          user: { team_id: { _eq: teamIdNum } },
        },
      });
      const entriesList = entriesRes?.user_revenue_entries || [];

      setTarget(totalTarget);
      setEntries(entriesList);
    } catch (err) {
      setErrorMsg(err?.message || 'Veri yuklenirken hata olustu');
      setTarget(0);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [isAllowed, teamIdNum, yil, ceyrek]);

  useEffect(() => {
    fetchPanelData();
  }, [fetchPanelData]);

  // Hedefe esas ciro: tutar * coefficient
  const totalActual = useMemo(() => {
    return entries.reduce((sum, e) => {
      const coef = coefficientById.get(e.bonus_revenue_type_id) ?? 1;
      return sum + Number(e.tutar || 0) * coef;
    }, 0);
  }, [entries, coefficientById]);

  const ratePct = useMemo(() => {
    if (!target || target <= 0) return 0;
    return (totalActual / target) * 100;
  }, [target, totalActual]);

  const contributions = useMemo(() => {
    const byUser = new Map();
    entries.forEach((e) => {
      const uid = e.user_id;
      if (!byUser.has(uid)) {
        byUser.set(uid, {
          user_id: uid,
          first_name: e.user?.first_name || '',
          last_name: e.user?.last_name || '',
          total: 0,
        });
      }
      const coef = coefficientById.get(e.bonus_revenue_type_id) ?? 1;
      byUser.get(uid).total += Number(e.tutar || 0) * coef;
    });
    const arr = Array.from(byUser.values());
    arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [entries, coefficientById]);

  // Renk + ikon + mesaj ratePct'e gore degisir. Ikonlar duygu spektrumu
  // ile esleniyor (mutsuz->mutlu) — kullaniciya gauge'a ek hizli sinyal.
  const statusInfo = useMemo(() => {
    if (ratePct >= 100) return {
      color: KP_PALETTE.GREEN,
      label: t('kontrolPaneli.emojiLabels.celebration'),
      Icon: CelebrationRoundedIcon,
    };
    if (ratePct >= 80) return {
      color: KP_PALETTE.GREEN,
      label: t('kontrolPaneli.emojiLabels.happy'),
      Icon: SentimentVerySatisfiedRoundedIcon,
    };
    if (ratePct >= 60) return {
      color: KP_PALETTE.ACCENT,
      label: t('kontrolPaneli.emojiLabels.happy'),
      Icon: SentimentSatisfiedRoundedIcon,
    };
    if (ratePct >= 30) return {
      color: KP_PALETTE.AMBER,
      label: t('kontrolPaneli.emojiLabels.neutral'),
      Icon: SentimentDissatisfiedRoundedIcon,
    };
    return {
      color: '#d64545',
      label: t('kontrolPaneli.emojiLabels.sad'),
      Icon: SentimentVeryDissatisfiedRoundedIcon,
    };
  }, [ratePct, t]);

  // Yetki yoksa hicbir sey gosterme.
  if (!isAllowed) {
    return null;
  }

  const teamOptions = (teams || []).map((tm) => ({ value: tm.id, label: tm.name }));

  return (
    <Box sx={styles.card}>
      {/* Header */}
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box sx={styles.headerIcon}>
            <EmojiEventsIcon sx={{ fontSize: '1.15rem' }} />
          </Box>
          <Box>
            <Box sx={styles.headerTitleRow}>
              <Typography sx={styles.headerTitle}>
                {t('kontrolPaneli.title')}
              </Typography>
              <Box sx={styles.independentChip}>
                {t('kontrolPaneli.ownFilterChip', 'Bağımsız filtre')}
              </Box>
            </Box>
            <Typography sx={styles.headerSubtitle}>
              {t('kontrolPaneli.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.headerRight}>
          <Box sx={styles.filterGroup}>
            <Typography sx={styles.filterPrefix}>
              {t('kontrolPaneli.filterLabels.quarter', 'Çeyrek:')}
            </Typography>
            <FormControl size="small" sx={styles.filterControl}>
              <Select
                value={selectedYilCeyrek}
                onChange={(e) => setSelectedYilCeyrek(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarMonthIcon sx={styles.filterAdornment} />
                  </InputAdornment>
                }
              >
                {QUARTER_OPTIONS.map((opt) => (
                  <MenuItem key={`${opt.yil}-${opt.ceyrek}`} value={`${opt.yil}-${opt.ceyrek}`}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Ekip dropdown'u yalnizca ROOT'a gosterilir. Diger roller kendi
              team_id'sini otomatik kullanir; manuel degistiremezler. */}
          {isRoot && (
            <Box sx={styles.filterGroup}>
              <Typography sx={styles.filterPrefix}>
                {t('kontrolPaneli.filterLabels.team', 'Ekip:')}
              </Typography>
              <FormControl size="small" sx={styles.filterControl}>
                <Select
                  value={selectedTeamId}
                  displayEmpty
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsIcon sx={styles.filterAdornment} />
                    </InputAdornment>
                  }
                  renderValue={(v) => {
                    if (!v) return t('kontrolPaneli.filters.team');
                    const tm = teamOptions.find((opt) => String(opt.value) === String(v));
                    return tm?.label || t('kontrolPaneli.filters.team');
                  }}
                >
                  {teamOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      {t('kontrolPaneli.filters.noTeams', 'Ekip bulunamadi')}
                    </MenuItem>
                  )}
                  {teamOptions.map((opt) => (
                    <MenuItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={styles.loadingBox}>
          <CircularProgress size={28} sx={{ color: KP_PALETTE.ACCENT }} />
        </Box>
      )}

      {!loading && errorMsg && (
        <Box sx={styles.errorBox}>{errorMsg}</Box>
      )}

      {!loading && !errorMsg && (
        <Box sx={styles.body}>
          {/* Sol: Gauge */}
          <Box sx={styles.gaugeWrap}>
            <Box sx={styles.gaugeBox}>
              <Gauge pct={ratePct} color={statusInfo.color} />
              <Box sx={styles.gaugeCenter}>
                <Typography sx={styles.gaugeValue}>
                  %{ratePct.toFixed(1)}
                </Typography>
                <Typography sx={styles.gaugeLabel}>
                  Gerçekleşme / Oran
                </Typography>
              </Box>
            </Box>
            <Box sx={styles.statusPill(statusInfo.color)}>
              <statusInfo.Icon sx={{ fontSize: 17 }} />
              {statusInfo.label}
            </Box>
          </Box>

          {/* Orta: Metric cards */}
          <Box sx={styles.metricsCol}>
            <Box sx={styles.metricCard('neutral')}>
              <Typography sx={styles.metricLabel}>
                {t('kontrolPaneli.cards.target')}
              </Typography>
              <Typography sx={styles.metricValue('neutral')}>
                {formatMoney(target)}
                <Typography component="span" sx={styles.currency}>TL</Typography>
              </Typography>
            </Box>

            <Box sx={styles.metricCard('success')}>
              <Typography sx={styles.metricLabel}>
                {t('kontrolPaneli.cards.actual')}
              </Typography>
              <Typography sx={styles.metricValue('success')}>
                {formatMoney(totalActual)}
                <Typography component="span" sx={styles.currency}>TL</Typography>
              </Typography>
            </Box>
          </Box>

          {/* Sağ: Kişi katkıları */}
          <Box sx={styles.contribCol}>
            <Box sx={styles.contribHeader}>
              <Typography sx={styles.contribTitle}>
                {t('kontrolPaneli.contribution.title')}
              </Typography>
              <Box sx={styles.contribBadge}>
                {contributions.length} kişi
              </Box>
            </Box>

            {contributions.length === 0 ? (
              <Box sx={styles.contribEmpty}>
                {t('kontrolPaneli.contribution.empty', 'Bu donemde kayit bulunamadi.')}
              </Box>
            ) : (
              <Stack spacing={1.75}>
                {contributions.map((c) => {
                  const userPct = totalActual > 0 ? (c.total / totalActual) * 100 : 0;
                  const fullName =
                    `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
                    `Kullanici #${c.user_id}`;
                  const initials = (
                    (c.first_name?.[0] || '') + (c.last_name?.[0] || '')
                  ).toUpperCase() || '?';
                  return (
                    <Box key={c.user_id} sx={styles.contribRow}>
                      <Box sx={styles.contribTop}>
                        <Avatar sx={styles.contribAvatar}>{initials}</Avatar>
                        <Typography sx={styles.contribName} noWrap>
                          {fullName}
                        </Typography>
                        <Typography sx={styles.contribAmount}>
                          {formatMoney(c.total)} TL
                        </Typography>
                      </Box>
                      <Box sx={styles.contribTrack}>
                        <Box sx={styles.contribFill(userPct)} />
                      </Box>
                      <Typography sx={styles.contribPct}>
                        Toplam ciro içindeki payı %{userPct.toFixed(1)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}

            <Box sx={styles.contribInfo}>
              <InfoOutlinedIcon sx={{ fontSize: '0.95rem', mt: '1px', color: '#8b95a5' }} />
              <Box>Hedef tanımlandığında prim hak edişleri otomatik hesaplanır.</Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CiroKontrolPaneli;
